import { onlineManager } from '@tanstack/react-query';
import { ApiResponse, ApisauceInstance, create } from 'apisauce';
import { AxiosError, AxiosHeaders, AxiosRequestConfig } from 'axios';
import AppEnvironment from '../components/enums/appEnvironment.enum';
import config from '../config';
import type { AppExceptionResponseData } from '../hooks/useHandleAppException';
import { URL_TO_VALIDATE_IF_USER_IS_ONLINE } from '../utils/network';
import {
  PATH_TO_SYNC_OFFLINE_ORDER,
  PATH_TO_VALIDATE_SYNC_STATUS_OF_ORDERS,
} from './modules/orders/orders.repository';
import { GetTeamsDataResponse } from './modules/teams/teams.types';
import {
  OAuthLoginParams,
  OAuthLoginResponse,
  RecoverPasswordParams,
  RecoverPasswordResponse,
} from './types';

export const restApi: ApisauceInstance = create({
  baseURL: config.baseURL,
});

const defaultUsersPath =
  '/admin/users?role=ADMIN_MARCA&role=ADMIN_LOJA&role=VENDEDOR&role=COMPRADOR';

export const BRAND_ID_HEADER_KEY = 'x-brand-id';
export const BRAND_ID_STORAGE_KEY = 'brand-id-context';
export const TEAM_ID_HEADER_KEY = 'x-team-id';
export const ADMIN_TEAM_HEADER_KEY = 'x-admin-team-id';
export const APP_ENVIRONMENT_HEADER_KEY = 'x-app-environment';
export const CLARITY_ID_HEADER_KEY = 'x-clarity-id';
export const SSO_REAUTH_DEADLINE_HEADER_KEY = 'x-sso-reauth-deadline';
export const SESSION_ID_HEADER_KEY = 'x-session-id';

export const isRequestFromCurrentSession = (requestConfig?: AxiosRequestConfig): boolean =>
  requestConfig?.headers?.[SESSION_ID_HEADER_KEY] === restApi.headers[SESSION_ID_HEADER_KEY];

const api = {
  setAdminTeam: (token: string) => restApi.setHeader(ADMIN_TEAM_HEADER_KEY, token),
  setTeam: (token: string) => restApi.setHeader(TEAM_ID_HEADER_KEY, token),
  setPublicId: (token: string) => restApi.setHeader('x-public-id', token),
  setToken: (token: string) => restApi.setHeader(SESSION_ID_HEADER_KEY, token),
  setAppEnvironment: (environment: AppEnvironment) =>
    restApi.setHeader(APP_ENVIRONMENT_HEADER_KEY, environment),
  setBrandContext: (brandId?: string) => {
    if (brandId) {
      restApi.setHeader(BRAND_ID_HEADER_KEY, brandId);
    } else {
      restApi.deleteHeader(BRAND_ID_HEADER_KEY);
    }
  },
  setBrandContextInSession: (brandId?: string) => {
    if (brandId) {
      sessionStorage.setItem(BRAND_ID_STORAGE_KEY, brandId);
    } else {
      sessionStorage.removeItem(BRAND_ID_STORAGE_KEY);
    }
  },
  getBrandContextFromSession: (): string | undefined => {
    const stored = sessionStorage.getItem(BRAND_ID_STORAGE_KEY);
    return stored || undefined;
  },
  getTeams: (
    loginBrandId?: string,
  ): Promise<ApiResponse<GetTeamsDataResponse, { message: string }>> =>
    restApi.get('/users/teams/v1', { loginBrandId, limit: 999 }),
  getAllUsers: (text?: string) =>
    text ? restApi.get(`${defaultUsersPath}&nameOrEmail=${text}`) : restApi.get(defaultUsersPath),
  getTeam: () => restApi.get('/users/team/v1'),
  getTeamById: (id: string) => restApi.get(`/users/admin/team/${id}`),
  ping: () => restApi.get('/auth'),
  login: (credentials: any): Promise<any> => restApi.post('/auth/login', credentials),
  logout: (): Promise<any> => restApi.post('/auth/logout'),
  resetSessionHeaders: () => {
    restApi.deleteHeader(TEAM_ID_HEADER_KEY);
    restApi.deleteHeader(BRAND_ID_HEADER_KEY);
    restApi.deleteHeader(ADMIN_TEAM_HEADER_KEY);
    restApi.deleteHeader(APP_ENVIRONMENT_HEADER_KEY);
    sessionStorage.removeItem(BRAND_ID_STORAGE_KEY);
  },
  resetPublicId: () => {
    restApi.deleteHeader('x-public-id');
  },
  oauthLogin: (data: OAuthLoginParams) =>
    restApi.post<OAuthLoginResponse>('/oauth/login', data, { withCredentials: true }),
  oauthConsent: (data: { oauth_request_id: string; granted: boolean }) =>
    restApi.post<{ redirect_to: string }>('/oauth/consent', data, { withCredentials: true }),
  recoverPassword: (data: RecoverPasswordParams) =>
    restApi.post<RecoverPasswordResponse, AppExceptionResponseData>('/auth/recover-password', data),
  resetPassword: (data: any) => restApi.post('/auth/reset-password', data),
  setInterceptorToOfflineMode: () => {
    restApi.axiosInstance.interceptors.request.use((config) => {
      const ACCEPTED_ENDPOINTS_IN_OFFLINE_MODE = [
        PATH_TO_SYNC_OFFLINE_ORDER,
        URL_TO_VALIDATE_IF_USER_IS_ONLINE,
        PATH_TO_VALIDATE_SYNC_STATUS_OF_ORDERS,
      ];

      if (
        !onlineManager.isOnline() &&
        (!config.url || !ACCEPTED_ENDPOINTS_IN_OFFLINE_MODE.includes(config.url))
      ) {
        const axiosError: AxiosError = {
          config,
          isAxiosError: false,
          name: 'OfflineModeError',
          message: 'modo offline ativado: requisições bloqueadas',
          toJSON: () => ({}),
          response: {
            status: 0,
            config: { headers: new AxiosHeaders() },
            headers: {},
            statusText: 'offline',
            data: { message: 'modo offline ativado: requisições bloqueadas' },
          },
        };
        return Promise.reject(axiosError);
      }

      return config;
    });
  },
};

api.setBrandContextInSession(undefined);

restApi.axiosInstance.interceptors.request.use((config) => {
  const brandId = api.getBrandContextFromSession();
  if (brandId && !config.headers[BRAND_ID_HEADER_KEY]) {
    config.headers[BRAND_ID_HEADER_KEY] = brandId;
  }
  return config;
});

export default api;
